import { defineComponent as _defineComponent } from 'vue'
import { library } from '@fortawesome/fontawesome-svg-core';
import { faBars, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faLayerPlus } from '@fortawesome/pro-solid-svg-icons';
import { onMounted, ref, watch, withDefaults } from 'vue';

import InitI18n from '@/initI18n';

import FormCheckbox from '@/shared/components/form/FormCheckbox.vue';
import FormFieldRadioGroup from '@/shared/components/form/FormFieldRadioGroup.vue';
import FormRadio from '@/shared/components/form/FormRadio.vue';

import { ColorFieldItem, FieldColor, LayerItem, LayerType, MenuItem, MenuItemId } from '../types';

interface MapMenuSettings {
  activeFieldColor: FieldColor;
  menuItems: MenuItem[];
  colorFieldItems: ColorFieldItem[];
  layerItems: LayerItem[];
  showTimestamps: boolean;
  alwaysShowFieldName: boolean;
}

/* -- Props -- */

interface Props {
  featureFieldsNDVIMeanEnabled: boolean;
  featureLandCadastresEnabled: boolean;
  featureGISObjectsEnabled: boolean;
  hideLayers?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MapMenu',
  props: {
    featureFieldsNDVIMeanEnabled: { type: Boolean, default: false },
    featureLandCadastresEnabled: { type: Boolean, default: false },
    featureGISObjectsEnabled: { type: Boolean, default: false },
    hideLayers: { type: Boolean, default: false }
  },
  emits: ["onActiveLayersChanged", "onFieldColorChanged", "onMenuItemClicked", "alwaysShowFieldName", "showTimestamps"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'onActiveLayersChanged', items: LayerItem[]): void;
  (e: 'onFieldColorChanged', fieldColor: FieldColor): void;
  (e: 'onMenuItemClicked', id: string): void;
  (e: 'alwaysShowFieldName', value: boolean): void;
  (e: 'showTimestamps', value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { featureFieldsNDVIMeanEnabled: boolean, featureLandCadastresEnabled: boolean, featureGISObjectsEnabled: boolean, hideLayers: boolean }

library.add(faLayerPlus, faXmark, faBars);

const t = InitI18n.translate;



/* -- State -- */

const isExpanded = ref<boolean>(false);
const menuSettings = ref<MapMenuSettings>({
  activeFieldColor: FieldColor.CROP,
  menuItems: [],
  layerItems: [{ title: t('Feldnutzung') || '', selected: false, type: LayerType.FIELD_UTILIZATION }],
  colorFieldItems: [
    { title: t('Standard'), value: FieldColor.DEFAULT },
    { title: t('nach Kultur'), value: FieldColor.CROP },
    { title: t('nach Sorte'), value: FieldColor.VARIETY },
    { title: t('nach letzter Tätigkeit'), value: FieldColor.ACTIVITY },
  ],
  alwaysShowFieldName: false,
  showTimestamps: false,
});

/* -- Watchers -- */

watch(
  menuSettings,
  (settings: MapMenuSettings) => {
    saveSettingsInLocalStorage(settings);
  },
  { deep: true },
);

/* -- Emits -- */



/* -- Lifecycle -- */

onMounted(() => {
  if (props.featureFieldsNDVIMeanEnabled) {
    menuSettings.value.colorFieldItems.push({ title: t('nach NDVI'), value: FieldColor.NDVI });
    menuSettings.value.colorFieldItems.push({ title: t('nach MBI'), value: FieldColor.MBI });
  }

  if (localStorage.mapMenuSettings) {
    // init settings
    const settings = JSON.parse(localStorage.mapMenuSettings) as MapMenuSettings;
    menuSettings.value.alwaysShowFieldName = settings.alwaysShowFieldName || false;
    menuSettings.value.activeFieldColor = settings.activeFieldColor || FieldColor.CROP;

    if (settings.layerItems) {
      menuSettings.value.layerItems = settings.layerItems;
    }

    // add/remove land cadastres layer if feature is enabled
    const landCadastreIndex = menuSettings.value.layerItems.findIndex(
      (item: LayerItem) => item.type === LayerType.LAND_CADASTRES,
    );
    if (props.featureLandCadastresEnabled && landCadastreIndex < 0) {
      addLandCadastreCheckbox();
    } else if (!props.featureLandCadastresEnabled && landCadastreIndex > 0) {
      menuSettings.value.layerItems.splice(landCadastreIndex, 1);
    }

    // add/remove gis layer if feature is enabled
    const isoZoneIndex = menuSettings.value.layerItems.findIndex(
      (item: LayerItem) => item.type === LayerType.ISOLATION_ZONES,
    );
    if (props.featureGISObjectsEnabled && isoZoneIndex < 0) {
      addGisLayerCheckboxes();
    } else if (!props.featureGISObjectsEnabled && isoZoneIndex > 0) {
      menuSettings.value.layerItems.splice(isoZoneIndex, 1);
      const mgIndex = menuSettings.value.layerItems.findIndex(
        (item: LayerItem) => item.type === LayerType.MULTIPLIER_GROUPS,
      );
      menuSettings.value.layerItems.splice(mgIndex, 1);
    }
  } else {
    if (props.featureLandCadastresEnabled) {
      addLandCadastreCheckbox();
    }

    if (props.featureGISObjectsEnabled) {
      addGisLayerCheckboxes();
    }
  }

  // trigger updates
  emit('onFieldColorChanged', menuSettings.value.activeFieldColor);
  emit('onActiveLayersChanged', menuSettings.value.layerItems);
  emit('alwaysShowFieldName', menuSettings.value.alwaysShowFieldName);
  emit('showTimestamps', menuSettings.value.showTimestamps);

  // add gis menu items if feature is enabled
  if (props.featureGISObjectsEnabled) {
    menuSettings.value.menuItems.push({ id: MenuItemId.CREATE_REPORT, title: t('Report') || '' });
    menuSettings.value.menuItems.push({ id: MenuItemId.ISOLATION_ZONES, title: t('Isolationszonen') || '' });
    menuSettings.value.menuItems.push({ id: MenuItemId.MULTIPLIER_GROUPS, title: t('Vermehrungsgemeinschaft') || '' });
  }
});

/* -- Methods -- */

function toggleMenu(): void {
  isExpanded.value = !isExpanded.value;
}

function menuItemClicked(menuItem: MenuItem): void {
  isExpanded.value = false;
  emit('onMenuItemClicked', menuItem.id);
}

function saveSettingsInLocalStorage(settings: MapMenuSettings): void {
  localStorage.mapMenuSettings = JSON.stringify(settings);
}

function addLandCadastreCheckbox(): void {
  menuSettings.value.layerItems.push({
    title: t('Grundstückskataster') || '',
    selected: false,
    type: LayerType.LAND_CADASTRES,
  });
}

function addGisLayerCheckboxes(): void {
  menuSettings.value.layerItems.push({
    title: t('Isolationszonen') || '',
    selected: false,
    type: LayerType.ISOLATION_ZONES,
  });

  menuSettings.value.layerItems.push({
    title: t('Vermehrungsgemeinschaften') || '',
    selected: false,
    type: LayerType.MULTIPLIER_GROUPS,
  });
}

return { __sfc: true,t, props, isExpanded, menuSettings, emit, toggleMenu, menuItemClicked, saveSettingsInLocalStorage, addLandCadastreCheckbox, addGisLayerCheckboxes, FormCheckbox, FormFieldRadioGroup, FormRadio }
}

})